import React, { useEffect, useState, useContext, useRef, useId, useCallback } from "react";
import { ItemContext } from "../../context/ItemContext";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { fetchData } from "../api";
import { Typography, Card, CardContent, Box, IconButton } from "@mui/material";
import Button from "@mui/material/Button";
import InputLabel from "@mui/material/InputLabel";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import Popup from "./Popup";
import CheckboxGroup from "./CheckboxGroup";
import CheckboxGroupMultiple from "./CheckBoxMultiple";
import "../../style/item-details.css";
import { useLocation } from "react-router-dom";

const ItemDetails = () => {
  const updatedItemsRef = useRef(null); // Initialize the ref with null
  const { id } = useParams();
  const [item, setItem] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [selectedItems, setSelectedItems] = useState([]);
  const [option, setOption] = useState([]);
  const [optionPrice, setOptionPrice] = useState(0);
  const [isRequiredSelected, setRequiredSelected] = useState(false);
  const [showError, setShowError] = useState(false);
  const [totalPriceLocal, setTotalPriceLocal] = useState(0);

  const [showPopup, setShowPopup] = useState(false);

  const { submitOrder, userDetails, cart, isEditMode, updateCartItem } =
    useContext(ItemContext);

  const navigate = useNavigate();

  const uniqueId = useId();

  const location = useLocation();

  const routeChangeCheckout = () => {
    if (isEditMode) {
      // Find the cart item we're editing
      // const existingItem = cart.find((cartItem) => cartItem.item.id === id);

      const existingItem = cart.find((cartItem) => cartItem.cartItemId === location.state?.cartItemId);


      if (existingItem) {
        // Update instead of adding
        updateCartItem(existingItem, updatedItemsRef.current[0]);
      }
    } else {
      submitOrder(updatedItemsRef.current, totalPriceLocal, userDetails);
    }
    let path = `/order`;
    navigate(path);
  };

  const routeChangeBack = () => {
    let path = `/menu`;
    navigate(path);
  };

  // Create a memoized function to calculate the total price
  const calculateTotalPrice = useCallback(() => {
    const itemsTotal = selectedItems.reduce((acc, selectedItem) => {
      const itemPrice =
        selectedItem.item && selectedItem.item.amount
          ? selectedItem.item.amount / 100
          : 0;
      const itemQuantity = selectedItem.quantity || 0;
      return acc + itemPrice * itemQuantity;
    }, 0);

    const optionsTotal = optionPrice / 100;

    return itemsTotal + optionsTotal;
  }, [selectedItems, optionPrice]);

  // Calculate and update total price whenever dependencies change
  useEffect(() => {
    const newTotalPrice = calculateTotalPrice();
    setTotalPriceLocal(newTotalPrice);
  }, [selectedItems, optionPrice, calculateTotalPrice]);

  const onItemClick = (item, isSelected) => {
    setSelectedItems((prevSelectedItems) => {
      const existingItem = prevSelectedItems.find(
        (selectedItem) => selectedItem.item.id === item.id
      );

      let updatedItems;

      if (isSelected) {
        if (existingItem) {
          // Item already exists in the selected items, increase its quantity
          updatedItems = prevSelectedItems.map((selectedItem) =>
            selectedItem.item.id === item.id
              ? {
                  ...selectedItem,
                  quantity: selectedItem.quantity + 1,
                }
              : selectedItem
          );
        } else {
          // Item doesn't exist in the selected items, add it with quantity 1
          updatedItems = [...prevSelectedItems, { item, quantity: 1 }];
        }
      } else {
        if (existingItem && existingItem.quantity > 0) {
          // Item exists in the selected items, decrease its quantity
          updatedItems = prevSelectedItems.map((selectedItem) =>
            selectedItem.item.id === item.id
              ? { ...selectedItem, quantity: selectedItem.quantity - 1 }
              : selectedItem
          );
        }
      }

      return updatedItems || prevSelectedItems;
    });
  };

  useEffect(() => {
    fetchData(`/item/${id}`)
      .then((response) => {
        setItem(response.item);
        onItemClick(response.item, true);

        if (isEditMode && cart) {
          const existingItem = cart.find(
            (cartItem) => cartItem.cartItemId === location.state?.cartItemId
          );
          if (existingItem) {
            setItem(existingItem.item);
            setQuantity(existingItem.item.quantity);

            setSelectedItems([
              ...selectedItems,
              {
                item: existingItem.item,
                quantity: quantity,
              },
            ]);

            // Properly handle options
            if (
              existingItem.item.options &&
              existingItem.item.options.length > 0
            ) {
              setOption(existingItem.item.options);

              const localOptionsTotal = existingItem.item.options.reduce(
                (total, option) => total + parseFloat(option.price || 0),
                0
              );
              setOptionPrice(localOptionsTotal);
            }

            // If there are options in the item, assume required options are selected
            if (
              existingItem.item.options &&
              existingItem.item.options.length > 0
            ) {
              setRequiredSelected(true);
            }
          }
        } else {
          // For non-edit mode, calculate option price
          const localOptionsTotal = option.reduce(
            (total, option) => total + parseFloat(option.price || 0),
            0
          );
          setOptionPrice(localOptionsTotal);
        }
      })
   
      .catch((error) => console.error("Error fetching item details:", error));
  }, []);

  if (!item || item.length === 0 || item === undefined) {
    return <div className="loading-container">Loading...</div>;
  }

  const handleIncreaseQuantity = () => {
    const newQuantity = quantity + 1;
    setQuantity(newQuantity);

    // Update all selected items with new quantity
    setSelectedItems((prevSelectedItems) =>
      prevSelectedItems.map((selectedItem) =>
        selectedItem.item.id === item.id
          ? { ...selectedItem, quantity: newQuantity }
          : selectedItem
      )
    );
  };

  const handleDecreaseQuantity = () => {
    if (quantity > 1) {
      const newQuantity = quantity - 1;
      setQuantity(newQuantity);

      // Update all selected items with new quantity
      setSelectedItems((prevSelectedItems) =>
        prevSelectedItems.map((selectedItem) =>
          selectedItem.item.id === item.id
            ? { ...selectedItem, quantity: newQuantity }
            : selectedItem
        )
      );
    } else {
      alert("You cannot select less than 1 item");
    }
  };

  const handleOptionChange = (option, selected) => {
    if (selected) {
      setOption((prevOptions) => {
        const optionExists = prevOptions.some(
          (opt) => opt.name === option.name
        );

        if (!optionExists) {
          const newOptionPrice =
            prevOptions.reduce(
              (total, opt) => total + parseFloat(opt.price || 0),
              0
            ) + parseFloat(option.price || 0);
          setOptionPrice(newOptionPrice);
          return [...prevOptions, option];
        }

        return prevOptions;
      });
    } else {
      setOption((prevOptions) => {
        const filteredOptions = prevOptions.filter(
          (opt) => opt.name !== option.name
        );

        // Calculate new option price when removing an option
        const newOptionPrice = filteredOptions.reduce(
          (total, opt) => total + parseFloat(opt.price || 0),
          0
        );

        setOptionPrice(newOptionPrice);
        return filteredOptions;
      });
    }
  };

  const handleOrderSubmit = () => {
    if (!isRequiredSelected) {
      setShowError(true);
      return;
    } else {
      setSelectedItems((prevItems) => {
        const updatedItems = prevItems.map((selectedItem) => {
          return {
            cartItemId: uniqueId,
            item: {
              ...selectedItem.item,
              options: option,
              quantity: quantity,
            },
          };
        });
        updatedItemsRef.current = updatedItems;
        return updatedItems;
      });

      if (selectedItems.length === 0) {
        alert("Order is empty, please click plus button to add to order");
      } else {
        setShowPopup(true);
        setShowError(false);
      }
    }
  };

  // Removed totalQuantity calculation as it's no longer displayed separately

  // Navigate back to menu without adding to cart (for bottom button)
  const handleBackToMenu = () => {
    setShowPopup(false);
    routeChangeBack();
  };

  // Continue shopping - adds to cart and goes back to menu (for popup)
  const handleContinueShopping = () => {
    if (isEditMode) {
      const existingItem = cart.find((cartItem) => cartItem.cartItemId === location.state?.cartItemId);
      if (existingItem) {
        updateCartItem(existingItem, updatedItemsRef.current[0]);
      }
    } else {
      submitOrder(updatedItemsRef.current, totalPriceLocal, userDetails);
    }
    setShowPopup(false);
    routeChangeBack();
  };

  const handleRequiredOptions = (isSelected) => {
    setRequiredSelected(isSelected);
    setShowError(false);
  };

  const keys = ["TYPE 1", "TYPE 2", "Type", "TYPE", "TYPE 2"];
  const selectedKey = keys.find((key) => item.modifiers[key]);
  const details = selectedKey ? item.modifiers[selectedKey][0].details : [];

  return (
    <Card className="item-details" elevation={0}>
      {/* Image Section */}
    {console.log("Item", item)}
      {item.imageUrl && (
        <div className="item-image-container">
          <img
            className="item-image"
            src={item.imageUrl}
            alt={item.name}
          />
        </div>
      )}

      {/* Header Section */}
      <div className="item-header">
        <Typography className="item-name" variant="h4" component="h1">
          {item.name}
        </Typography>
        <Typography className="item-description" variant="body1" color="text.secondary">
          {item.description}
        </Typography>
      </div>

      {/* Options Sections */}
      <CardContent sx={{ padding: 0 }}>
        {/* Base Selection */}
        <CardContent className="option-list">
          <Box sx={{ minWidth: 120 }}>
            <InputLabel id="base-select-label">🍚 Base</InputLabel>
            <CheckboxGroup
              priceOfItem={item.amount}
              handleOptionChange={handleRequiredOptions}
              onTotalOptionChange={handleOptionChange}
              isEditMode={isEditMode}
              editOptions={isEditMode ? option : null}
              options={
                item.modifiers["Donburi TYPE"]
                  ? item.modifiers["Donburi TYPE"][0].details
                  : []
              }
            />
          </Box>
        </CardContent>

        {/* Extra Options */}
        <CardContent className="option-list">
          <Box sx={{ minWidth: 100 }}>
            <InputLabel id="extra-select-label">➕ Extra</InputLabel>
            <CheckboxGroupMultiple
              onTotalOptionChange={handleOptionChange}
              isEditMode={isEditMode}
              editOptions={isEditMode ? option : null}
              options={
                item.modifiers.Option ? item.modifiers.Option[0].details : []
              }
            />
          </Box>
        </CardContent>

        {/* Protein Selection */}
        <CardContent className="option-list">
          <Box sx={{ minWidth: 120 }}>
            <InputLabel id="protein-select-label">🍖 Protein</InputLabel>
            <CheckboxGroup
              handleOptionChange={handleRequiredOptions}
              editOptions={isEditMode ? option : null}
              isEditMode={isEditMode}
              onTotalOptionChange={handleOptionChange}
              priceOfItem={item.amount}
              options={details}
            />
          </Box>
        </CardContent>

        {/* Special Sauce Selection */}
        {/* <CardContent className="option-list">
          <Box sx={{ minWidth: 120 }}>
            <InputLabel id="sauce-select-label">🌶️ Special Sauce</InputLabel>
            <CheckboxGroup
              handleOptionChange={handleRequiredOptions}
              editOptions={isEditMode ? option : null}
              isEditMode={isEditMode}
              onTotalOptionChange={handleOptionChange}
              priceOfItem={item.amount}
              options={
                item.modifiers["Special Sauce"]
                  ? item.modifiers["Special Sauce"][0].details
                  : []
              }
            />
          </Box>
        </CardContent> */}
      </CardContent>

      {/* Quantity Controls */}
      <div className="quantity-controls">
        <IconButton 
          aria-label="remove" 
          onClick={handleDecreaseQuantity}
          sx={{
            backgroundColor: 'error.main',
            color: 'white',
            '&:hover': {
              backgroundColor: 'error.dark',
            },
            width: 48,
            height: 48
          }}
        >
          <RemoveIcon />
        </IconButton>
        <div className="quantity-display">
          {quantity}
        </div>
        <IconButton 
          aria-label="add" 
          onClick={handleIncreaseQuantity}
          sx={{
            backgroundColor: 'success.main',
            color: 'white',
            '&:hover': {
              backgroundColor: 'success.dark',
            },
            width: 48,
            height: 48
          }}
        >
          <AddIcon />
        </IconButton>
      </div>

      {/* Price Section */}
      <div className="price-section">
        <div className="price-label">Total Price</div>
        <div className="price-amount">${totalPriceLocal.toFixed(2)}</div>
        <div className="quantity-info">
          {quantity} {quantity === 1 ? 'item' : 'items'} in order
        </div>
      </div>

      {/* Error Message */}
      {showError && (
        <div className="error-message">
          ⚠️ Please select the base and protein before adding to cart.
        </div>
      )}
      {/* Action Buttons */}
      <div className="action-buttons">
        <Button
          className="btn-back"
          variant="outlined"
          size="large"
          onClick={handleBackToMenu}
        >
          ← Back to Menu
        </Button>
        <Button
          className="btn-add-cart"
          variant="contained"
          size="large"
          onClick={handleOrderSubmit}
        >
          {isEditMode ? '✓ Update Cart' : '🛒 Add to Cart'}
        </Button>
      </div>
      <Popup
        open={showPopup}
        onClose={() => setShowPopup(false)}
        onKeepShopping={handleContinueShopping}
        onGoToCart={routeChangeCheckout}
      />
    </Card>
  );
};

export default ItemDetails;
